import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';

const STRANDED_SUBJECT = "Let's get you connected to your squad";
const strandedMessage = (firstName) => `Hi ${firstName},

Thanks for signing up for CircleUpSquad! We noticed you're not connected to a care circle yet.

If someone invited you, just reply to this email with the invite code they shared (or the name of the person you're supporting) and we'll get you connected right away.

You can also click your invite link again and sign in — you'll be added automatically.

Warmly,
Shannon Ivey
Founder, CircleUpSquad`;

const EmailUserDialog = ({ open, onOpenChange, user }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && user) {
      const firstName = (user.name || 'friend').split(' ')[0];
      if (user.circle_count === 0) {
        setSubject(STRANDED_SUBJECT);
        setMessage(strandedMessage(firstName));
      } else {
        setSubject('');
        setMessage(`Hi ${firstName},\n\n`);
      }
    }
  }, [open, user]);

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) { toast.error('Subject and message are required'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/users/${user.id}/email`, { subject, message });
      toast.success(res.data.message);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send email'); }
    setSending(false);
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="surface-card max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Mail className="w-4 h-4 text-cyan-600" /> Email {user.name || user.email}
          </DialogTitle>
          <DialogDescription>
            To: {user.email} · Replies go to shannon@shannon-ivey.com
            {user.circle_count === 0 && <span className="text-amber-600 font-medium"> · Not in a circle</span>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-user-subject" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Message</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={9} className="text-sm" data-testid="email-user-message" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancel</Button>
            <Button onClick={handleSend} disabled={sending} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="email-user-send-btn">
              {sending ? 'Sending...' : 'Send Email'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailUserDialog;
